/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from "@angular/core";
import { PostgrestError, SupabaseClient, createClient } from "@supabase/supabase-js";
import { AppointmentDto } from "src/app/calendar/interfaces/appointment_dto";
import {
  convertAppointment,
  convertAppointmentDto,
  convertAppointmentsDto,
} from "src/app/utils/ConvertAppointments";
import { environment } from "src/environments/environment";
import { StudentDto } from "src/app/students/interfaces/student_dto";
import { CustomerDto } from "src/app/auth/interfaces/customer_dto";
import { InvoiceAddRequest } from "src/app/office/interfaces/invoice_add_request";
import { ToastController } from "@ionic/angular";
import { StudentAddRequest } from "src/app/students/interfaces/student_add_request";
import { PaymentAddRequest } from "src/app/office/interfaces/payment_add_request";
import { PaymentDto } from "src/app/office/interfaces/payment_dto";
import { PaymentBalance } from "src/app/office/interfaces/payment_balance";
import { StudentUpdateRequest } from "src/app/students/interfaces/student_update_request";
import { SendAppointmentRequest } from "src/app/calendar/interfaces/send_appointment_request";
import {
  updateAppointment,
  updateAppointmentSuccess,
  updateLesson,
  updateLessonSuccess,
} from "src/app/calendar/ngrx/calendar.actions";
import { Store } from "@ngrx/store";
import { AppointmentAddRequest } from "src/app/calendar/interfaces/appointment_add_request";
import { ReminderService } from "./reminder.service";
import { EducationCategoryDto } from "src/app/settings/interfaces/education_category_dto";
import { EducationTitleDto } from "src/app/settings/interfaces/education_title_dto";
import { LessonRatingDto } from "src/app/lessons/interfaces/lesson_rating";
import { EducationBodyDto } from "src/app/settings/interfaces/education_body_dto";
import { FileService } from "./files.service";
import { DomSanitizer } from "@angular/platform-browser";
import { LocationTrackingDto } from "src/app/tracking/interfaces/location_tracking";
import { CalendarResourceDto } from "src/app/calendar/interfaces/calendar_resource_dto";
import { InvoiceStatusDto } from "src/app/office/interfaces/invoice_status_dto";
import { Base64Service } from "src/app/shared/services/base64.service";
import { RenameSpecialChars } from "src/app/utils/RenameSpecialChars";
import { ReportGeneratorDto } from "src/app/report-generation/interfaces/report_generator_dto";
import { AuthStorage } from "src/app/core/storage/auth-storage.class";
import { CapacitorHttp } from "@capacitor/core";
import { ServiceDto } from "src/app/settings/interfaces/service_dto";
import { SubscriptionDto } from "src/app/settings/interfaces/subscription_dto";
import { updateStudent } from "src/app/students/ngrx/students.actions";
import { AuthService } from "./auth.service";
import { Observable, from, of } from "rxjs";
import { SupabaseClientService } from "./supabase-client";
import { InvoiceDto } from "src/app/office/interfaces/invoice_dto";
import { ProductDto } from "src/app/products/interfaces/product_dto";

export const FILE_DB_PROFILE_PICTURE = "profile_pictures";
export const FILE_DB_INVOICE = "invoices";
export const FILE_DB_ATTACHMENTS = "attachments";
export const FILE_DB_REPORTS = "reports";

@Injectable({
  providedIn: "root",
})
export class DataService {
  private supabase: SupabaseClient;

  constructor(
    private toastController: ToastController,
    private store: Store<any>,
    private reminderService: ReminderService,
    private fileService: FileService,
    private readonly dom: DomSanitizer,
    private base64Service: Base64Service,
    private authService: AuthService,
    private supabaseClientService: SupabaseClientService
  ) {
    this.supabase = supabaseClientService.supabaseClient;
  }

  from(table: string) {
    return this.supabase.from(table);
  }

  getInvoices(): Observable<any> {
    const userId = this.authService.getCurrentUserId();

    if (!userId) {
      return of([]);
    }
    return from(
      this.from("invoice")
        .select(
          "*, student(*), appointment(*), invoice_item(*), invoice_status(*), invoice_payment(*)"
        )
        .eq("user_id", userId)
        .then((response) => {
          if (response.error) {
            throw response.error;
          }
          return response.data;
        })
    );
  }

  getStudent(student_id: number): Observable<StudentDto> {
    return from(
      this.from("student")
        .select(
          "*, invoice(*, invoice_status(*), invoice_payment(*)), appointment(*, location_tracking(*)), payment(*), lesson_rating(*)"
        )
        .eq("student_id", student_id)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }

          const student = data.data[0];
          if (student.has_profile_picture) {
            const userId = this.authService.getCurrentUserId();
            student.profile_picture_url = `${environment.supabaseClientConfig.supabaseUrl}/storage/v1/object/public/profile_pictures/${userId}/${student.student_id}.png`;
          }

          student.appointment = convertAppointmentsDto(student.appointment);
          return student as StudentDto;
        })
    );
  }

  getAppointments(): Observable<AppointmentDto[]> {
    const userId = this.authService.getCurrentUserId();

    if (!userId) {
      return of([]);
    }
    return from(
      this.from("appointment")
        .select(
          "*, student(*), invoice(*, invoice_status(*), invoice_payment(*))"
        )
        .order("start_time", { ascending: false })
        .eq("user_id", userId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          // convert data to Appointment[]
          return convertAppointmentsDto(data.data);
        })
    );
  }

  getUpcomingLessons(): Observable<AppointmentDto[]> {
    const userId = this.authService.getCurrentUserId();
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    if (!userId) {
      throw new Error("User not found");
    }
    return from(
      // get all the appointments from supabase where the start_date is in the future and sort them by start_date
      this.from("appointment")
        .select(
          "*, student(*), invoice(*, invoice_status(*), invoice_payment(*))"
        )
        .gt("start_time", currentDate.toISOString())
        .order("start_time")
        .eq("user_id", userId)
        .then(async (data) => {
          if (data.error) {
            throw data.error;
          }

          // add the profile picture url to the students
          const lessons = await Promise.all(
            data.data.map(async (appointment) => {
              if (appointment.student.has_profile_picture) {
                appointment.student.profile_picture_url = `${environment.supabaseClientConfig.supabaseUrl}/storage/v1/object/public/profile_pictures/${userId}/${appointment.student.student_id}.png`;
              }
              return appointment;
            })
          );

          // convert data to Appointment[]
          return convertAppointmentsDto(lessons);
        })
    );
  }

  addLesson(
    addAppointmentDto: AppointmentAddRequest
  ): Observable<AppointmentDto> {
    const sendReminder = addAppointmentDto.sendReminder;
    const student = addAppointmentDto.student;

    let appointment = { ...addAppointmentDto };
    delete appointment.sendReminder;
    delete appointment.student;

    return from(
      this.supabase
        .from("appointment")
        .insert(appointment)
        .select("*, student(*)")
        .then(async (data) => {
          if (data.error) {
            throw data.error;
          }

          const lesson = convertAppointmentDto(data.data[0]);

          if (sendReminder) {
            let reminder = {
              appointment: lesson,
              student: student,
            };
            this.reminderService.sendReminder(reminder);
          }
          return lesson;
        })
    );
  }

  updateLesson(
    updateAppointmentDto: AppointmentAddRequest
  ): Observable<AppointmentDto> {
    const sendReminder = updateAppointmentDto.sendReminder;
    const student = updateAppointmentDto.student;

    let appointment = { ...updateAppointmentDto };
    delete appointment.sendReminder;
    delete appointment.student;

    this.supabase.auth.getUser().then((user) => {
      appointment.user_id = user.data.user!.id;
    });

    return from(
      this.supabase
        .from("appointment")
        .update(appointment)
        .eq("appointment_id", appointment.appointment_id)
        .select("*, student(*)")
        .then(async (data) => {
          if (data.error) {
            throw data.error;
          }

          const updatedAppointment = convertAppointmentDto(data.data[0]);
          if (sendReminder) {
            let reminder = {
              appointment: updateAppointment,
              student: student,
            };
            this.reminderService.sendReminder(reminder);
          }

          return updatedAppointment;
        })
    );
  }

  deleteAppointment(appointmentId: number): Observable<AppointmentDto> {
    return from(
      this.supabase
        .from("appointment")
        .delete()
        .eq("appointment_id", appointmentId)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }

          return convertAppointmentDto(data.data[0]);
        })
    );
  }

  updateAppointment(appointment: AppointmentDto): Observable<AppointmentDto> {
    const updateAppointmentRequest = convertAppointment(appointment);
    this.supabase.auth.getUser().then((user) => {
      updateAppointmentRequest.user_id = user.data.user!.id;
    });

    return from(
      this.supabase
        .from("appointment")
        .update(updateAppointmentRequest)
        .eq("appointment_id", appointment.Id)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          if (data.data && (data.data as any).length > 0) {
            return convertAppointmentDto(data.data[0]);
          }
          throw new Error("No data found");
        })
    );
  }

  addLessonRatings(
    lessonRatings: LessonRatingDto[]
  ): Observable<LessonRatingDto[]> {
    let lessonRatingsRequest = [...lessonRatings]; // Spread into a new array
    let lessonRatingsToUpdate: LessonRatingDto[] = [];
    let lessonRatingsToInsert: LessonRatingDto[] = [];
    let updatedLessonRatings: LessonRatingDto[] = [];
    let insertedLessonRatings: LessonRatingDto[] = [];

    const userId = this.authService.getCurrentUserId();

    // Separate lessonRatings into two arrays based on lesson_rating_id presence
    lessonRatingsRequest.forEach((rating) => {
      if (rating.lesson_rating_id !== null) {
        lessonRatingsToUpdate.push({
          ...rating,
          user_id: userId,
        });
      } else {
        const updatedRating = {
          ...rating,
          user_id: userId,
        };
        delete updatedRating.lesson_rating_id;
        lessonRatingsToInsert.push(updatedRating);
      }
    });

    let updatePromises =
      lessonRatingsToUpdate.length > 0
        ? this.supabase
            .from("lesson_rating")
            .upsert(lessonRatingsToUpdate)
            .select("*")
            .then((result) => {
              if (result.error) throw result.error;
              updatedLessonRatings = result.data as LessonRatingDto[];
            })
        : Promise.resolve();

    let insertPromises =
      lessonRatingsToInsert.length > 0
        ? this.supabase
            .from("lesson_rating")
            .upsert(lessonRatingsToInsert)
            .select("*")
            .then((result) => {
              if (result.error) throw result.error;
              insertedLessonRatings = result.data as LessonRatingDto[];
            })
        : Promise.resolve();

    return from(
      Promise.all([updatePromises, insertPromises]).then(() => {
        const combinedResults = [
          ...updatedLessonRatings,
          ...insertedLessonRatings,
        ];
        return combinedResults as LessonRatingDto[];
      })
    );
  }

  deleteLessonRatings(
    lessonRatings: LessonRatingDto[]
  ): Observable<LessonRatingDto[]> {
    const lessonRatingIds = lessonRatings.map(
      (rating) => rating.lesson_rating_id
    );

    return from(
      this.supabase
        .from("lesson_rating")
        .delete()
        .in("lesson_rating_id", lessonRatingIds)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as LessonRatingDto[];
        })
    );
  }

  getLessonRatings(): Observable<LessonRatingDto[]> {
    const userId = this.authService.getCurrentUserId();

    return from(
      this.supabase
        .from("lesson_rating")
        .select("*")
        .eq("user_id", userId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as LessonRatingDto[];
        })
    );
  }

  getLessonRatingsForStudent(studentId: number): Observable<LessonRatingDto[]> {
    return from(
      this.supabase
        .from("lesson_rating")
        .select("*")
        .eq("student_id", studentId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as LessonRatingDto[];
        })
    );
  }

  getLessonRatingsForLesson(lessonId: number): Observable<LessonRatingDto[]> {
    return from(
      this.supabase
        .from("lesson_rating")
        .select("*")
        .eq("appointment_id", lessonId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as LessonRatingDto[];
        })
    );
  }

  getCustomer(): Observable<CustomerDto> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      return of(null);
    }
    return from(
      this.from("customer")
        .select("*")
        .eq("user_id", userId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as CustomerDto;
        })
    );
  }

  updateCustomer(customer: CustomerDto): Observable<CustomerDto> {
    const customerToUpdate = { ...customer };

    return from(
      this.supabase
        .from("customer")
        .update(customerToUpdate)
        .eq("customer_id", customer.customer_id)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as CustomerDto;
        })
    );
  }

  disconnectGoogleCalendar(customerId: number): Observable<CustomerDto> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);
    const deleteResourcesPromise = userIdPromise.then((userId) => {
      return this.supabase
        .from("calendar_resource")
        .delete()
        .eq("user_id", userId)
        .eq("source", "google");
    });

    const updateCustomerPromise = this.supabase
      .from("customer")
      .update({
        customer_id: customerId,
        google_refresh_token: null,
      } as CustomerDto)
      .eq("customer_id", customerId)
      .select("*");

    return from(
      Promise.all([deleteResourcesPromise, updateCustomerPromise]).then(
        (results) => {
          const deleteResult = results[0];
          const updateResult = results[1];

          if (deleteResult.error) {
            throw deleteResult.error;
          }
          if (updateResult.error) {
            throw updateResult.error;
          }

          return updateResult.data[0] as CustomerDto;
        }
      )
    );
  }

  disconnectLocalCalendar(customerId: number): Observable<CustomerDto> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);
    const deleteResourcesPromise = userIdPromise.then((userId) => {
      return this.supabase
        .from("calendar_resource")
        .delete()
        .eq("user_id", userId)
        .eq("source", "local");
    });

    const updateCustomerPromise = this.supabase
      .from("customer")
      .update({
        customer_id: customerId,
        local_calendar_connected: false,
      } as CustomerDto)
      .eq("customer_id", customerId)
      .select("*");

    return from(
      Promise.all([deleteResourcesPromise, updateCustomerPromise]).then(
        (results) => {
          const deleteResult = results[0];
          const updateResult = results[1];

          if (deleteResult.error) {
            throw deleteResult.error;
          }
          if (updateResult.error) {
            throw updateResult.error;
          }

          return updateResult.data[0] as CustomerDto;
        }
      )
    );
  }

  getEducationCategories(): Observable<EducationCategoryDto[]> {
    const userId = this.authService.getCurrentUserId();

    return from(
      this.supabase
        .from("education_category")
        .select("*")
        .eq("user_id", userId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as EducationCategoryDto[];
        })
    );
  }

  addEducationCategory(
    category: EducationCategoryDto
  ): Observable<EducationCategoryDto> {
    const categoryToAdd = {
      ...category,
      user_id: this.authService.getCurrentUserId(),
    };
    return from(
      this.supabase
        .from("education_category")
        .insert(categoryToAdd)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as EducationCategoryDto;
        })
    );
  }

  updateEducationTitle(
    title: EducationTitleDto
  ): Observable<EducationTitleDto> {
    return from(
      this.supabase
        .from("education_title")
        .update(title)
        .eq("education_title_id", title.education_title_id)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as EducationTitleDto;
        })
    );
  }

  deleteEducationTitle(
    title: EducationTitleDto
  ): Observable<EducationTitleDto> {
    return from(
      this.supabase
        .from("education_title")
        .delete()
        .eq("education_title_id", title.education_title_id)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return title;
        })
    );
  }

  updateEducationBody(body: EducationBodyDto): Observable<EducationBodyDto> {
    return from(
      this.supabase
        .from("education_body")
        .update(body)
        .eq("education_body_id", body.education_body_id)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as EducationBodyDto;
        })
    );
  }

  deleteEducationBody(body: EducationBodyDto): Observable<EducationBodyDto> {
    return from(
      this.supabase
        .from("education_body")
        .delete()
        .eq("education_body_id", body.education_body_id)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return body;
        })
    );
  }

  updateEducationCategory(
    category: EducationCategoryDto
  ): Observable<EducationCategoryDto> {
    return from(
      this.supabase
        .from("education_category")
        .update(category)
        .eq("education_category_id", category.education_category_id)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as EducationCategoryDto;
        })
    );
  }

  deleteEducationCategory(
    category: EducationCategoryDto
  ): Observable<EducationCategoryDto> {
    return from(
      this.supabase
        .from("education_category")
        .delete()
        .eq("education_category_id", category.education_category_id)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return category;
        })
    );
  }

  getEducationTitles(educationCategoryId?: number): Observable<any[]> {
    const userId = this.authService.getCurrentUserId();

    if (!educationCategoryId) {
      return from(
        this.supabase
          .from("education_title")
          .select("*")
          .eq("user_id", userId)
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data;
          })
      );
    }

    return from(
      this.supabase
        .from("education_title")
        .select("*")
        .eq("education_category_id", educationCategoryId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  getEducationBodies(educationTitleId?: number): Observable<any[]> {
    const userId = this.authService.getCurrentUserId();

    if (!educationTitleId) {
      return from(
        this.supabase
          .from("education_body")
          .select("*")
          .eq("user_id", userId)
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data;
          })
      );
    }

    return from(
      this.supabase
        .from("education_body")
        .select("*")
        .eq("education_title_id", educationTitleId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  addEducationTitle(title: EducationTitleDto): Observable<EducationTitleDto> {
    const titleToAdd = {
      ...title,
      user_id: this.authService.getCurrentUserId(),
    };
    return from(
      this.supabase
        .from("education_title")
        .insert(titleToAdd)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as EducationTitleDto;
        })
    );
  }

  addEducationBody(body: EducationBodyDto): Observable<EducationBodyDto> {
    const bodyToAdd = { ...body, user_id: this.authService.getCurrentUserId() };
    return from(
      this.supabase
        .from("education_body")
        .insert(bodyToAdd)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as EducationBodyDto;
        })
    );
  }

  updateEducationTitles(
    titles: EducationTitleDto[]
  ): Observable<EducationTitleDto[]> {
    return from(
      this.supabase
        .from("education_title")
        .upsert(titles)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as EducationTitleDto[];
        })
    );
  }

  updateEducationBodies(
    bodies: EducationBodyDto[]
  ): Observable<EducationBodyDto[]> {
    return from(
      this.supabase
        .from("education_body")
        .upsert(bodies)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as EducationBodyDto[];
        })
    );
  }

  getStudents(): Observable<StudentDto[]> {
    const userId = this.authService.getCurrentUserId();

    if (!userId) {
      return of([]);
    }

    return from(
      this.from("student")
        .select("*")
        .eq("user_id", userId)
        .then(async (data) => {
          if (data.error) {
            throw data.error;
          }

          const students = await Promise.all(
            data.data.map(async (student) => {
              if (student.has_profile_picture) {
                student.profile_picture_url = `${environment.supabaseClientConfig.supabaseUrl}/storage/v1/object/public/profile_pictures/${userId}/${student.student_id}.png`;
              }
              return student;
            })
          );

          return students as StudentDto[];
        })
    );
  }

  addStudent(
    student: StudentAddRequest,
    profile_picture?: string,
    attachments?: File[]
  ) {
    let studentToAdd = { ...student };
    if (attachments) {
      attachments.forEach(async (attachment) => {
        if (!studentToAdd.attachments) {
          studentToAdd.attachments = [];
        }
        var fileName = RenameSpecialChars(attachment.name);
        studentToAdd.attachments = [...studentToAdd.attachments, fileName];
      });
    }
    let insertPromise: any;

    if (profile_picture) {
      studentToAdd.has_profile_picture = true;
      insertPromise = this.supabase
        .from("student")
        .insert(studentToAdd)
        .select("*");
    } else {
      insertPromise = this.supabase
        .from("student")
        .insert(studentToAdd)
        .select("*");
    }

    return from(
      insertPromise.then(async (data) => {
        if (data.error) {
          this.toastController
            .create({
              message: "Schüler konnte nicht hinzugefügt werden",
              duration: 1500,
              position: "bottom",
            })
            .then((toast) => toast.present());
          throw data.error;
        }

        let newStudent = data.data[0];
        if (profile_picture) {
          const profile_picture_url = await this.fileService.uploadCameraFile(
            FILE_DB_PROFILE_PICTURE,
            profile_picture,
            {
              path: `${this.authService.getCurrentUserId()}/${
                newStudent.student_id
              }.png`,
              title: "",
            }
          );

          newStudent.profile_picture_url =
            this.dom.bypassSecurityTrustResourceUrl(
              URL.createObjectURL(profile_picture_url)
            );
        }

        if (attachments) {
          this.uploadAttachments(
            attachments,
            this.authService.getCurrentUserId(),
            newStudent.student_id
          );
        }

        return newStudent;
      })
    );
  }

  updateStudent(
    student: Partial<StudentUpdateRequest>,
    profile_picture?: string,
    attachments?: File[]
  ) {
    let studentToUpdate = { ...student };
    if (attachments) {
      attachments.forEach(async (attachment) => {
        if (!studentToUpdate.attachments) {
          studentToUpdate.attachments = [];
        }
        var fileName = RenameSpecialChars(attachment.name);
        studentToUpdate.attachments = [
          ...studentToUpdate.attachments,
          fileName,
        ];
      });
    }

    let updatePromise = null;

    if (profile_picture) {
      studentToUpdate.has_profile_picture = true;
      updatePromise = this.supabase
        .from("student")
        .update(studentToUpdate)
        .eq("student_id", studentToUpdate.student_id)
        .select("*");
    } else {
      updatePromise = this.supabase
        .from("student")
        .update(studentToUpdate)
        .eq("student_id", studentToUpdate.student_id)
        .select("*");
    }

    return from(
      updatePromise.then(async (data) => {
        if (data.error) {
          this.toastController
            .create({
              message: "Schüler konnte nicht aktualisiert werden",
              duration: 1500,
              position: "bottom",
            })
            .then((toast) => toast.present());
          throw data.error;
        }

        let updatedStudent = data.data[0] as any;

        if (profile_picture) {
          const profilePictureBlob = await this.fileService.uploadCameraFile(
            FILE_DB_PROFILE_PICTURE,
            profile_picture,
            {
              path: `${this.authService.getCurrentUserId()}/${
                updatedStudent.student_id
              }.png`,
              title: "",
            }
          );

          updatedStudent.profile_picture_url =
            this.dom.bypassSecurityTrustResourceUrl(
              URL.createObjectURL(profilePictureBlob)
            );
        }

        if (attachments) {
          this.uploadAttachments(
            attachments,
            this.authService.getCurrentUserId(),
            updatedStudent.student_id
          );
        }

        return updatedStudent;
      })
    );
  }

  deleteStudent(student: StudentDto): Observable<StudentDto> {
    const deletions = [];

    if (student.has_profile_picture) {
      deletions.push(
        this.supabase.storage
          .from(FILE_DB_PROFILE_PICTURE)
          .remove([`${student.user_id}/${student.student_id}.png`])
      );
    }

    if (student.attachments) {
      student.attachments.forEach((attachment) => {
        deletions.push(
          this.supabase.storage
            .from(FILE_DB_ATTACHMENTS)
            .remove([`${student.user_id}/${student.student_id}/${attachment}`])
        );
      });
    }

    deletions.push(
      this.supabase
        .from("student")
        .delete()
        .eq("student_id", student.student_id)
    );

    return from(
      Promise.all(deletions).then((results) => {
        results.forEach((result) => {
          if (result.error) {
            throw result.error;
          }
        });
        return student;
      })
    );
  }

  deleteAttachment(
    student: StudentDto,
    attachment: string
  ): Observable<string> {
    return from(
      this.supabase
        .from("student")
        .update({
          attachments: student.attachments.filter(
            (a: string) => a != attachment
          ),
        })
        .eq("student_id", student.student_id)
        .then(() => {
          return this.supabase.storage
            .from(FILE_DB_ATTACHMENTS)
            .remove([`${student.user_id}/${student.student_id}/${attachment}`])
            .then((result) => {
              if (result.error) {
                throw result.error;
              }
              return attachment;
            });
        })
    );
  }

  getPaymentBalance(student_id: number): Observable<PaymentBalance[]> {
    return from(
      this.from("payment")
        .select("*, invoice_payment(*)")
        .eq("student_id", student_id)
        .is("invoice_id", null)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }

          let openPayments: PaymentBalance[] = [];
          data.data.forEach((payment: any) => {
            const invoicePayments = payment.invoice_payment;
            const totalInvoicePaymentAmount = invoicePayments.reduce(
              (total: number, invoicePayment: any) =>
                total + invoicePayment.amount,
              0
            );

            if (totalInvoicePaymentAmount < payment.amount) {
              let newPaymentBalance: PaymentBalance = {
                payment_id: payment.payment_id,
                date: payment.date,
                student_id: payment.student_id,
                open_amount: payment.amount - totalInvoicePaymentAmount,
              };
              openPayments.push(newPaymentBalance);
            }
          });

          return openPayments;
        })
    );
  }

  getOpenInvoiceItems(student_id: number): Observable<any[]> {
    return from(
      this.from("appointment")
        .select(
          "prize, subject, type, appointment_id, start_time, end_time, lessons, subscription_payment"
        )
        .eq("student_id", student_id)
        .is("invoice_id", null)
        .order("start_time")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }

          // convert the data to InvoiceItemDto[]
          const invoiceItems = data.data.map((appointment: any) => {
            return {
              id: appointment.appointment_id,
              appointment_id: appointment.appointment_id,
              prize: appointment.prize,
              name: appointment.type || appointment.subject,
              date: appointment.start_time.split("T")[0],
              amount: appointment.lessons,
              unit: appointment.lessons > 1 ? "Lektionen" : "Lektion",
              subscription_payment: appointment.subscription_payment,
              initial_prize: appointment.prize,
            };
          });

          return invoiceItems;
        })
    );
  }

  addInvoice(
    invoiceAddRequest: InvoiceAddRequest,
    status?: string,
    file?: File
  ): Observable<any> {
    const invoice = {
      customer_id: invoiceAddRequest.customer_id,
      due_date: invoiceAddRequest.due_date,
      invoice_date: invoiceAddRequest.invoice_date,
      student_id: invoiceAddRequest.student?.student_id,
      paid_amount: invoiceAddRequest.paid_amount,
      total_amount: invoiceAddRequest.total_amount,
      amount: invoiceAddRequest.amount,
      subscription_lessons: invoiceAddRequest.subscription_lessons,
      user_id: invoiceAddRequest.user_id,
    };

    return from(
      this.supabase
        .from("invoice")
        .insert(invoice)
        .select("*")
        .then(async (data) => {
          if (data.error) {
            throw data.error;
          }
          var newInvoice = data.data[0];
          newInvoice.invoice_status = [];
          newInvoice.student = invoiceAddRequest.student;
          newInvoice.invoice_payment = [];

          // add payment with the invoice_id
          try {
            if (invoiceAddRequest.payment[0].amount > 0) {
              const payment = {
                amount: invoiceAddRequest.payment[0].amount,
                date: invoiceAddRequest.payment[0].date,
                payment_method: invoiceAddRequest.payment[0].payment_method,
                invoice_id: newInvoice.invoice_id,
                user_id: invoiceAddRequest.user_id,
                student_id: invoiceAddRequest.student?.student_id,
              };

              const paymentData = await this.supabase
                .from("payment")
                .insert(payment)
                .select("*");
              if (paymentData.error) {
                throw paymentData.error;
              }
              const newPayment = paymentData.data[0];
              newPayment.student = invoiceAddRequest.student;
              newInvoice.payments = [newPayment];
              newInvoice.paid_amount += newPayment.amount;
              newInvoice.invoice_payment = [
                {
                  amount: newPayment.amount,
                  date: newPayment.date,
                  payment_id: newPayment.payment_id,
                  invoice_id: newInvoice.invoice_id,
                  created_at: new Date(),
                  user_id: invoiceAddRequest.user_id,
                },
              ];
            }
          } catch (err) {
            this.toastController
              .create({
                message: "Fehler beim Hinzufügen der Zahlung",
                duration: 1500,
                position: "bottom",
              })
              .then((toast) => toast.present());
            throw err;
          }

          try {
            // add the invoice_payments with the invoice_id
            if (
              invoiceAddRequest.invoice_payment.length !== 0 &&
              invoiceAddRequest.invoice_payment[0].amount > 0
            ) {
              const invoicePayments = invoiceAddRequest.invoice_payment.map(
                (invoicePayment) => {
                  return {
                    amount: invoicePayment.amount,
                    date: invoicePayment.date,
                    payment_id: invoicePayment.payment_id,
                    invoice_id: newInvoice.invoice_id,
                    user_id: invoiceAddRequest.user_id,
                  };
                }
              );
              const invoicePaymentData = await this.supabase
                .from("invoice_payment")
                .insert(invoicePayments)
                .select("*");
              if (invoicePaymentData.error) {
                throw invoicePaymentData.error;
              }

              const newInvoiceItems = invoicePaymentData.data;
              newInvoice.invoice_payment.push(...newInvoiceItems);
            }
          } catch (err) {
            this.toastController
              .create({
                message: "Fehler beim Hinzufügen der Rechnungspositionen",
                duration: 1500,
                position: "bottom",
              })
              .then((toast) => toast.present());
            throw err;
          }

          try {
            // update the appointments with the invoice_id
            invoiceAddRequest.invoice_item.forEach(async (invoiceItem) => {
              if (invoiceItem.appointment_id) {
                let appointmentToUpdate = {
                  invoice_id: newInvoice.invoice_id,
                  appointment_id: invoiceItem.appointment_id,
                };
                this.store.dispatch(
                  updateLesson({ lesson: appointmentToUpdate })
                );
              }
            });

            if (
              invoiceAddRequest.student_subscription_lessons !==
              invoiceAddRequest.student.subscription_lessons
            ) {
              const student = {
                subscription_lessons:
                  invoiceAddRequest.student_subscription_lessons,
                student_id: invoiceAddRequest.student.student_id,
              };
              this.store.dispatch(updateStudent({ student: student }));
            }

            if (invoiceAddRequest.invoice_item.length !== 0) {
              const invoiceItems = invoiceAddRequest.invoice_item.map(
                (invoiceItem) => {
                  return {
                    appointment_id: invoiceItem.appointment_id,
                    subscription_id: invoiceItem.subscription_id,
                    invoice_id: newInvoice.invoice_id,
                    name: invoiceItem.name,
                    prize: invoiceItem.prize,
                    unit: invoiceItem.unit,
                    amount: invoiceItem.amount,
                    description: invoiceItem.description,
                    date: invoiceItem.date,
                    user_id: invoiceAddRequest.user_id,
                  };
                }
              );

              const invoiceItemData = await this.supabase
                .from("invoice_item")
                .insert(invoiceItems)
                .select("*");
              if (invoiceItemData.error) {
                throw invoiceItemData.error;
              }

              const newInvoiceItems = invoiceItemData.data;
              newInvoice.invoice_item = newInvoiceItems;
            }

            if (status) {
              await this.addInvoiceStatus(
                newInvoice.invoice_id,
                status
              ).toPromise();
              newInvoice.invoice_status = [{ status }];
            }

            // upload the file to supabase
            if (file) {
              await this.fileService.uploadFile(FILE_DB_INVOICE, file, {
                path: `${this.authService.getCurrentUserId()}/${
                  newInvoice.invoice_id
                }.pdf`,
                title: "",
              });
            }

            return newInvoice;
          } catch (err) {
            this.toastController
              .create({
                message: "Fehler beim Abschluss der Rechnung",
                duration: 1500,
                position: "bottom",
              })
              .then((toast) => toast.present());
            throw err;
          }
        })
    );
  }

  deleteInvoice(invoiceId: number): Observable<any> {
    this.fileService.deleteFile(FILE_DB_INVOICE, `${this.authService.getCurrentUserId()}/${invoiceId}.pdf`);
    return from(
      this.supabase
        .from("invoice")
        .delete()
        .eq("invoice_id", invoiceId)
        .select("*, student(*), appointment(*)")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }

          const invoice = data.data[0] as InvoiceDto;
          if (invoice.subscription_lessons && invoice.student) {
            const student = {
              subscription_lessons: invoice.student.subscription_lessons + invoice.subscription_lessons,
              student_id: invoice.student.student_id,
            };
            this.store.dispatch(updateStudent({ student: student }));
          }
          if (invoice.appointment) {
            for (const appointment of invoice.appointment) {
              const appointmentDto = convertAppointmentDto(appointment);
              appointmentDto.Invoice = null;
              appointmentDto.InvoiceId = null;
              this.store.dispatch(updateLessonSuccess({ lesson: appointmentDto }));
            }
          }
          return data.data;
        })
    );
  }

  addPayment(payment: PaymentAddRequest): Observable<PaymentDto> {
    const paymentToAdd = {
      amount: payment.amount,
      date: payment.date,
      payment_method: payment.payment_method,
      invoice_id: payment.invoice_id,
      user_id: payment.user_id,
      student_id: payment.student.student_id,
    };

    return from(
      this.supabase
        .from("payment")
        .insert(paymentToAdd)
        .select("*, student(*)")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as PaymentDto;
        })
    );
  }

  deletePayment(payment_id: number): Observable<any> {
    return from(
      this.supabase.from("payment").delete().eq("payment_id", payment_id).select("*").then((data) => {
        if (data.error) {
          throw data.error;
        }
        return data.data[0] as PaymentDto;
      })
    );
  }

  sendAppointmentReminder(
    sendAppointmentRequest: SendAppointmentRequest
  ): Observable<any> {
    return from(
      this.supabase.functions
        .invoke("smtp2sendappointment", { body: sendAppointmentRequest })
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  addInvoiceStatus(
    invoice_id: number,
    status: string
  ): Observable<InvoiceStatusDto> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);
    const newStatus = {
      invoice_id,
      status,
      user_id: userIdPromise,
    };

    return from(
      userIdPromise.then((userId) => {
        return this.supabase
          .from("invoice_status")
          .insert({ ...newStatus, user_id: userId })
          .select("*")
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data[0] as InvoiceStatusDto;
          });
      })
    );
  }

  addCalendarResources(
    resources: CalendarResourceDto[]
  ): Observable<CalendarResourceDto[]> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);

    return from(
      userIdPromise.then((userId) => {
        const updatedResources = resources.map((resource) => ({
          ...resource,
          user_id: userId,
        }));
        return this.supabase
          .from("calendar_resource")
          .upsert(updatedResources)
          .select("*")
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data as CalendarResourceDto[];
          });
      })
    );
  }

  getCalendarResources(): Observable<CalendarResourceDto[]> {
    return from(
      this.from("calendar_resource")
        .select("*")
        .eq("user_id", this.authService.getCurrentUserId())
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as CalendarResourceDto[];
        })
    );
  }

  updateCalendarResource(
    resource: CalendarResourceDto
  ): Observable<CalendarResourceDto> {
    return from(
      this.supabase
        .from("calendar_resource")
        .update(resource)
        .eq("calendar_resource_id", resource.calendar_resource_id)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as CalendarResourceDto;
        })
    );
  }

  getPayments(): Observable<any[]> {
    if (!this.authService.getCurrentUserId()) {
      return of([]);
    }

    return from(
      this.from("payment")
        .select("*, student(*)")
        .eq("user_id", this.authService.getCurrentUserId())
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  getReports(): Observable<any[]> {
    return from(
      this.from("report_generator")
        .select("*")
        .order("created_at")
        .eq("user_id", this.authService.getCurrentUserId())
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  getReportConfigurations(): Observable<any[]> {
    return from(
      this.from("report_configuration")
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  deleteReport(report: ReportGeneratorDto): Observable<number> {
    return from(
      this.supabase
        .from("report_generator")
        .delete()
        .eq("report_generator_id", report.report_generator_id)
        .then(async (data) => {
          if (data.error) {
            throw data.error;
          }

          await this.supabase.storage
            .from(FILE_DB_REPORTS)
            .remove([`${report.user_id}/${report.report_generator_id}.pdf`])
            .then((result) => {
              if (result.error) {
                throw result.error;
              }
            });

          return report.report_generator_id;
        })
    );
  }

  getTrackings(): Observable<any[]> {
    return from(
      this.from("location_tracking")
        .select("*")
        .order("created_at", { ascending: false })
        .eq("user_id", this.authService.getCurrentUserId())
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  loadServices(): Observable<any[]> {
    return from(
      this.from("service")
        .select("*")
        .eq("user_id", this.authService.getCurrentUserId())
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  loadSubscriptions(): Observable<any[]> {
    return from(
      this.from("subscription")
        .select("*")
        .eq("user_id", this.authService.getCurrentUserId())
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data;
        })
    );
  }

  addTracking(tracking: LocationTrackingDto): Observable<LocationTrackingDto> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);

    return from(
      userIdPromise.then((userId) => {
        const updatedTracking = { ...tracking, user_id: userId };
        return this.supabase
          .from("location_tracking")
          .upsert(updatedTracking)
          .select("*")
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data[0] as LocationTrackingDto;
          });
      })
    );
  }

  replaceTrackings(
    oldTrackings: LocationTrackingDto[],
    newTrackings: Partial<LocationTrackingDto>[]
  ): Observable<LocationTrackingDto[]> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);
    // first try to add the new trackings and then delete the old ones (if adding was successful)
    return from(
      userIdPromise.then((userId) => {
        return this.supabase
          .from("location_tracking")
          .insert(
            newTrackings.map((tracking) => ({ ...tracking, user_id: userId }))
          )
          .select("*")
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return this.supabase
              .from("location_tracking")
              .delete()
              .in(
                "location_tracking_id",
                oldTrackings.map((tracking) => tracking.location_tracking_id)
              )
              .then((deleteData) => {
                if (deleteData.error) {
                  throw deleteData.error;
                }
                return data.data as LocationTrackingDto[];
              });
          });
      })
    );
  }

  addTrackingHttpPlugin(
    tracking: LocationTrackingDto
  ): Observable<LocationTrackingDto> {
    const userTokenPromise = this.supabase.auth
      .getSession()
      .then((session) => session.data.session?.access_token);
    const userId = this.authService.getCurrentUserId();

    return from(
      userTokenPromise.then((userToken) => {
        let trackingToAdd = { ...tracking, user_id: userId };

        if (trackingToAdd.location_tracking_id) {
          return this.supabase
            .from("location_tracking")
            .upsert(trackingToAdd)
            .select("*")
            .then((data) => {
              if (data.error) {
                throw data.error;
              }
              return data.data[0] as LocationTrackingDto;
            });
        } else {
          return CapacitorHttp.request({
            method: "POST",
            url: `${environment.supabaseClientConfig.supabaseUrl}/rest/v1/location_tracking?select=*`,
            headers: {
              Accept: "*/*",
              "Accept-Encoding": "gzip, deflate, br, zstd",
              "Accept-Language":
                "de-CH,de-DE;q=0.9,de;q=0.8,en-US;q=0.7,en;q=0.6",
              ApiKey: environment.supabaseClientConfig.supabaseKey,
              Authorization: `Bearer ${userToken}`,
              "Content-Profile": "public",
              "Content-Type": "application/json",
              Prefer: "resolution=merge-duplicates,return=representation",
            },
            data: trackingToAdd,
          }).then((response) => {
            if (response.status >= 400) {
              throw new Error("HTTP Fehler");
            }
            return response.data[0] as LocationTrackingDto;
          });
        }
      })
    );
  }

  deleteTracking(
    tracking: LocationTrackingDto
  ): Observable<LocationTrackingDto> {
    return from(
      this.supabase
        .from("location_tracking")
        .delete()
        .eq("location_tracking_id", tracking.location_tracking_id)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as LocationTrackingDto;
        })
    );
  }

  getTrackingsByAppointmentId(
    appointmentId: number
  ): Observable<LocationTrackingDto[]> {
    return from(
      this.from("location_tracking")
        .select("*")
        .eq("appointment_id", appointmentId)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as LocationTrackingDto[];
        })
    );
  }

  getGpsTrackingsByAppointmentId(
    appointmentId: number
  ): Observable<LocationTrackingDto[]> {
    return from(
      this.from("location_tracking")
        .select("*")
        .eq("appointment_id", appointmentId)
        .eq("comment", null)
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data as LocationTrackingDto[];
        })
    );
  }

  listFilesInStorageBucket(
    bucketName: string,
    path: string
  ): Observable<any[]> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);
    return from(
      userIdPromise.then((userId) => {
        return this.supabase.storage
          .from(bucketName)
          .list(`${userId}/${path}`)
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data;
          });
      })
    );
  }

  addReportGenerator(
    reportGenerator: ReportGeneratorDto
  ): Observable<ReportGeneratorDto> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);

    return from(
      userIdPromise.then((userId) => {
        return this.supabase
          .from("report_generator")
          .upsert({ ...reportGenerator, user_id: userId })
          .select("*")
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data[0] as ReportGeneratorDto;
          });
      })
    );
  }

  upsertProduct(product: ProductDto): Observable<ProductDto> {
    const userId = this.authService.getCurrentUserId();
    return from(
      this.supabase
        .from("product")
        .upsert({ ...product, user_id: userId })
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0] as ProductDto;
        })
    );
  }

  getProducts(): Observable<ProductDto[]> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);
    return from(
      userIdPromise.then((userId) => {
        return this.from("product")
          .select("*")
          .eq("user_id", userId)
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data as ProductDto[];
          })
      })
    );
  }

  deleteProduct(productId: number): Observable<number> {
    const userId = this.authService.getCurrentUserId();
    return from(
      this.supabase
        .from("product")
        .delete()
        .eq("product_id", productId)
        .eq("user_id", userId)
        .select("*")
        .then((data) => {
          return data.data[0].product_id;
        })
    );
  }

  upsertService(service: ServiceDto): Observable<ServiceDto> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);

    return from(
      userIdPromise.then((userId) => {
        return this.supabase
          .from("service")
          .upsert({ ...service, user_id: userId })
          .select("*")
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data[0] as ServiceDto;
          });
      })
    );
  }

  upsertSubscription(
    subscription: SubscriptionDto
  ): Observable<SubscriptionDto> {
    const userIdPromise = this.supabase.auth
      .getUser()
      .then((user) => user.data.user!.id);

    return from(
      userIdPromise.then((userId) => {
        return this.supabase
          .from("subscription")
          .upsert({ ...subscription, user_id: userId })
          .select("*")
          .then((data) => {
            if (data.error) {
              throw data.error;
            }
            return data.data[0] as SubscriptionDto;
          });
      })
    );
  }

  deleteService(serviceId: number): Observable<number> {
    return from(
      this.supabase
        .from("service")
        .delete()
        .eq("service_id", serviceId)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0].service_id;
        })
    );
  }

  deleteSubscription(subscriptionId: number): Observable<number> {
    return from(
      this.supabase
        .from("subscription")
        .delete()
        .eq("subscription_id", subscriptionId)
        .select("*")
        .then((data) => {
          if (data.error) {
            throw data.error;
          }
          return data.data[0].subscription_id;
        })
    );
  }

  private uploadAttachments(
    attachments: File[],
    user_id: string,
    student_id: string
  ) {
    try {
      attachments.forEach(async (attachment) => {
        var attachmentBase64 = await this.base64Service.convertFileToBase64(
          attachment
        );
        var fileName = RenameSpecialChars(attachment.name);

        await this.fileService.uploadFile(
          FILE_DB_ATTACHMENTS,
          attachmentBase64,
          {
            path: `${user_id}/${student_id}/${fileName}`,
            title: "",
          }
        );
      });
    } catch (err) {
      throw err;
    }
  }
}
